/**
 * ReportFindings.js — Forensic findings table with 5W+H event cards
 * WHO · WHAT · WHERE · WHEN · FROM WHERE · HOW
 */
import React, { useState, useMemo } from "react";
import { getMitre, getPortInfo, SEVERITY_COLORS, severityFromLabel } from "../reportUtils";

export default function ReportFindings({ data, stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats || !data.length) return null;

  const [viewMode,  setViewMode]  = useState("table");  // table | cards
  const [sevFilter, setSevFilter] = useState("all");
  const [page,      setPage]      = useState(1);
  const ROWS = 25;

  const findings = useMemo(() => {
    return data.map((row, idx) => {
      const attackType = stats.attackCol ? String(row[stats.attackCol] || "unknown").trim() : "unknown";
      const srcIP      = stats.srcIPCol  ? String(row[stats.srcIPCol]  || "").trim() : "";
      const dstIP      = stats.dstIPCol  ? String(row[stats.dstIPCol]  || "").trim() : "";
      const srcPort    = stats.srcPortCol ? row[stats.srcPortCol] : "";
      const dstPort    = stats.dstPortCol ? row[stats.dstPortCol] : "";
      const protocol   = stats.protoCol   ? String(row[stats.protoCol] || "").trim().toUpperCase() : "";
      const timestamp  = stats.timeCol    ? String(row[stats.timeCol]  || "").trim() : "";
      const bytes      = stats.bytesCol   ? row[stats.bytesCol] : "";

      const severity   = severityFromLabel(attackType);
      const mitre      = getMitre(attackType);
      const portInfo   = dstPort ? getPortInfo(dstPort) : null;

      return {
        id: idx + 1,
        attackType, srcIP, dstIP, srcPort, dstPort,
        protocol, timestamp, bytes, severity, mitre, portInfo,
        // 5W+H
        who:       srcIP || "Unknown source",
        what:      attackType,
        where:     dstIP ? `${dstIP}${dstPort ? `:${dstPort}` : ""}` : "Unknown target",
        when:      timestamp || "Unknown time",
        fromWhere: srcIP || "Unknown origin",
        how:       portInfo ? `${protocol} via ${portInfo.service} (${portInfo.app})` : protocol || "Unknown mechanism",
      };
    });
  }, [data, stats]);

  const filtered = useMemo(() => {
    if (sevFilter === "all") return findings;
    return findings.filter(f => f.severity === sevFilter);
  }, [findings, sevFilter]);

  const paginated  = filtered.slice((page - 1) * ROWS, page * ROWS);
  const totalPages = Math.ceil(filtered.length / ROWS);

  const sevCounts = useMemo(() => {
    const counts = { critical:0, high:0, medium:0, low:0, info:0 };
    findings.forEach(f => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
    return counts;
  }, [findings]);

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#FF8A65">Forensic Findings — 5W+H Analysis</SectionTitle>

      {/* Controls */}
      <div className="no-print" style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>
          {filtered.length.toLocaleString()} of {findings.length.toLocaleString()} events
        </div>

        {/* Severity filter */}
        <div style={{ display: "flex", gap: 6 }}>
          {["all","critical","high","medium","low","info"].map(sev => {
            const color = sev === "all" ? theme.colors.textMuted : SC[sev];
            const count = sev === "all" ? findings.length : sevCounts[sev];
            return (
              <button key={sev} onClick={() => { setSevFilter(sev); setPage(1); }} style={{
                padding: "4px 10px",
                background: sevFilter === sev ? `${color}22` : "transparent",
                border: `1px solid ${sevFilter === sev ? color : theme.colors.cardBorder}`,
                borderRadius: 4, cursor: "pointer", color,
                fontFamily: "monospace", fontSize: 10, fontWeight: 700
              }}>{sev.toUpperCase()} ({count})</button>
            );
          })}
        </div>

        {/* View toggle */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={() => setViewMode("table")} style={viewBtn(viewMode === "table", theme)}>TABLE</button>
          <button onClick={() => setViewMode("cards")} style={viewBtn(viewMode === "cards", theme)}>CARDS</button>
        </div>
      </div>

      {/* TABLE VIEW */}
      {viewMode === "table" && (
        <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
              <thead>
                <tr style={{ background: theme.colors.tableHeader, position: "sticky", top: 0, zIndex: 1 }}>
                  {["#","Severity","WHO (Src IP)","WHAT (Attack)","WHERE (Target)","WHEN","HOW (Protocol/App)","MITRE"].map(h => (
                    <th key={h} style={{ padding: "9px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 9, letterSpacing: 1, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((f, i) => {
                  const color = SC[f.severity] || SC.info;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                      <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 10 }}>{(page-1)*ROWS + i + 1}</td>
                      <td style={{ padding: "7px 10px" }}>
                        <span style={{ padding: "2px 6px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 3, color, fontSize: 9, fontWeight: 700 }}>{f.severity.toUpperCase()}</span>
                      </td>
                      <td style={{ padding: "7px 10px", color: "#C62828", fontFamily: "monospace", fontWeight: 700 }}>{f.who || "—"}</td>
                      <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{f.what}</td>
                      <td style={{ padding: "7px 10px", color: theme.colors.accent }}>{f.where || "—"}</td>
                      <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 10 }}>{f.when || "—"}</td>
                      <td style={{ padding: "7px 10px", color: theme.colors.accent, fontSize: 10 }}>{f.how || "—"}</td>
                      <td style={{ padding: "7px 10px", color: "#C62828", fontSize: 9 }}>{f.mitre.technique}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderTop: `1px solid ${theme.colors.cardBorder}`, fontFamily: "monospace", fontSize: 11 }}>
            <span style={{ color: theme.colors.textMuted }}>Page {page} of {totalPages} · {filtered.length.toLocaleString()} events</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[["◀◀",1],["◀",page-1],["▶",page+1],["▶▶",totalPages]].map(([label, target]) => (
                <button key={label} onClick={() => setPage(Math.max(1, Math.min(totalPages, target)))} style={{
                  padding: "5px 10px", background: theme.colors.tableHeader, border: `1px solid ${theme.colors.cardBorder}`,
                  borderRadius: 4, cursor: "pointer", fontFamily: "monospace", fontSize: 11, color: theme.colors.textPrimary
                }}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CARDS VIEW — 5W+H Forensic Cards */}
      {viewMode === "cards" && (
        <div>
          {paginated.filter(f => f.severity === "critical" || f.severity === "high").slice(0, 12).map((f, i) => {
            const color = SC[f.severity] || SC.info;
            return (
              <div key={i} className="report-card" style={{
                background: theme.colors.card, border: `1px solid ${color}33`,
                borderLeft: `4px solid ${color}`, borderRadius: 10,
                padding: 16, marginBottom: 12
              }}>
                {/* Card header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ padding: "3px 8px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4, color, fontFamily: "monospace", fontSize: 10, fontWeight: 700 }}>
                      {f.severity.toUpperCase()}
                    </span>
                    <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color: theme.colors.textPrimary }}>{f.attackType}</span>
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>
                    Event #{(page-1)*ROWS + i + 1} · {f.mitre.technique} — {f.mitre.tactic}
                  </div>
                </div>

                {/* 5W+H grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                  {[
                    { q: "WHO",       emoji: "👤", label: "Source (Attacker)",         value: f.who,       color: "#C62828" },
                    { q: "WHAT",      emoji: "⚡", label: "Attack Type / Signature",   value: f.what,      color: color     },
                    { q: "WHERE",     emoji: "🎯", label: "Target (IP:Port)",           value: f.where,     color: theme.colors.accent },
                    { q: "WHEN",      emoji: "🕐", label: "Timestamp",                  value: f.when,      color: "#F57F17" },
                    { q: "FROM WHERE",emoji: "🌐", label: "Origin IP",                  value: f.fromWhere, color: theme.colors.accent },
                    { q: "HOW",       emoji: "🔧", label: "Mechanism / Application",   value: f.how,       color: "#2E7D32" },
                  ].map(({ q, emoji, label, value, color: c }) => (
                    <div key={q} style={{ background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 6, padding: "8px 10px" }}>
                      <div style={{ fontFamily: "monospace", fontSize: 8, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 2 }}>{emoji} {q}</div>
                      <div style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: c || theme.colors.textPrimary, wordBreak: "break-all" }}>
                        {value || "—"}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Port context */}
                {f.portInfo && (
                  <div style={{ marginTop: 10, padding: "6px 10px", background: theme.colors.cardBorder, borderRadius: 4, fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>
                    Target port {f.dstPort}: <strong>{f.portInfo.service}</strong> — {f.portInfo.app} · Risk: <strong style={{ color: SC[f.portInfo.risk] || SC.info }}>{f.portInfo.risk.toUpperCase()}</strong>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination for cards */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
            {[["◀",page-1],["▶",page+1]].map(([label, target]) => (
              <button key={label} onClick={() => setPage(Math.max(1, Math.min(totalPages, target)))} style={{
                padding: "8px 20px", background: theme.colors.tableHeader, border: `1px solid ${theme.colors.cardBorder}`,
                borderRadius: 6, cursor: "pointer", fontFamily: "monospace", fontSize: 12, color: theme.colors.textPrimary
              }}>{label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const viewBtn = (active, theme) => ({
  padding: "5px 12px", background: active ? "rgba(21,101,192,0.1)" : theme.colors.tableHeader,
  border: `1px solid ${active ? "#1565C0" : theme.colors.cardBorder}`, borderRadius: 4,
  cursor: "pointer", fontFamily: "monospace", fontSize: 10,
  color: active ? "#1565C0" : theme.colors.textMuted, fontWeight: active ? 700 : 400
});

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
